import { Box } from "@mui/material";
import React, { useState } from "react";
import { Label } from "@/components/Label";
import { Button, ButtonVariantOption } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import type { AdditionalFieldType } from "@/types/additionalFields";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { FieldItem } from "./components/FieldItem";
import { AddFieldForm } from "./components/AddFieldForm";
import { DeletedFieldsModal } from "./components/DeletedFieldsModal";
import { formatNumberWithComma } from "@/utils/formatNumber";

/**
 * 保存済み地図プロット詳細のカスタム項目設定コンポーネントのプロパティ
 */
export interface SavedMapPlotRequestDetailAdditionalFieldsConfigProps {
  /** 追加フィールドのデータ */
  value: AdditionalFieldConfig | undefined;
  /** 追加フィールドデータが変更された際のコールバック関数 */
  onChange: (value: AdditionalFieldConfig) => void;
}

/**
 * 保存済み地図プロット詳細のカスタム項目設定コンポーネント
 *
 * 物件リスト保存時に追加で入力可能な項目の設定を行うためのコンポーネント。
 * 以下の機能を提供します：
 * - 既存の追加項目の表示・編集
 * - 既存の追加項目の削除
 * - 新規追加項目の作成（項目名と型を指定）
 * - 削除済み項目の復元
 *
 * @param props - コンポーネントのプロパティ
 * @param props.value - 追加フィールドのデータ
 * @param props.onChange - データ変更時のコールバック
 */
export const SavedMapPlotRequestDetailAdditionalFieldsConfig: React.FC<
  SavedMapPlotRequestDetailAdditionalFieldsConfigProps
> = ({ value, onChange }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);

  // 削除済み項目を分離
  const deletedFields =
    value?.fields?.filter((field) => !!field.deletedAt) ?? [];

  /**
   * 項目削除処理
   * 新規項目（id=""）の場合は物理削除、既存項目は論理削除
   */
  const handleDeleteField = (index: number): void => {
    if (!value?.fields) return;

    const fieldToDelete = value.fields[index];
    if (!fieldToDelete) return;

    // 新規項目（id=""）の場合は配列から削除
    if (fieldToDelete.id === "") {
      const updatedFields = value.fields.filter((_, i) => i !== index);
      onChange({ fields: updatedFields });
      return;
    }

    // 既存項目の場合は論理削除（deletedAtに現在時刻を設定）
    const updatedFields = value.fields.map((field, i) =>
      i === index ? { ...field, deletedAt: new Date().toISOString() } : field
    );

    onChange({ fields: updatedFields });
  };

  /**
   * 項目復元処理
   * deletedAtをnullに設定
   */
  const handleRestoreField = (deletedIndex: number): void => {
    if (!value?.fields) return;

    const fieldToRestore = deletedFields[deletedIndex];
    if (!fieldToRestore) return;

    const updatedFields = value.fields.map((field) =>
      field.id === fieldToRestore.id ? { ...field, deletedAt: null } : field
    );

    onChange({ fields: updatedFields });
  };

  /**
   * 新規項目追加処理
   */
  const handleAddField = (
    label: string,
    fieldType: AdditionalFieldType
  ): void => {
    const newField = {
      id: "", // バックエンドで生成されるため空文字列
      key: label,
      fieldType,
      createdAt: "", // バックエンドで生成されるため空文字列
      deletedAt: null,
    };

    onChange({
      fields: [...(value?.fields ?? []), newField],
    });

    setShowAddForm(false);
  };

  /**
   * 項目名編集開始処理
   */
  const handleStartEdit = (fieldId: string, _currentKey: string): void => {
    setEditingFieldId(fieldId);
  };

  /**
   * 項目名編集保存処理
   */
  const handleSaveEdit = (fieldId: string, newKey: string): void => {
    if (!value?.fields) return;

    const updatedFields = value.fields.map((field) =>
      field.id === fieldId ? { ...field, key: newKey } : field
    );

    onChange({ fields: updatedFields });

    // 編集モードを終了
    setEditingFieldId(null);
  };

  /**
   * 項目名編集キャンセル処理
   */
  const handleCancelEdit = (): void => {
    setEditingFieldId(null);
  };

  return (
    <Box>
      <Box sx={{ m: 2 }}>
        <Label>カスタム項目設定</Label>

        <Box sx={{ mb: 2 }}>
          {value?.fields?.map((field, index) => {
            // 削除済み項目はスキップ
            if (field.deletedAt) return null;

            const isEditing = editingFieldId === field.id;

            return (
              <FieldItem
                key={field.id || `field-${index}`}
                field={field}
                isEditing={isEditing}
                allFields={value.fields}
                onEdit={handleStartEdit}
                onSave={handleSaveEdit}
                onCancel={handleCancelEdit}
                onDelete={() => {
                  handleDeleteField(index);
                }}
              />
            );
          })}
        </Box>

        {/* 削除済み項目へのリンク */}
        {deletedFields.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Button
              label={`削除済み項目を表示 (${formatNumberWithComma(
                deletedFields.length
              )}件)`}
              variant={ButtonVariantOption.Text}
              size={SizingWrapperStyle.SMALL}
              onClick={() => {
                setShowDeletedModal(true);
              }}
            />
          </Box>
        )}

        {!showAddForm && (
          <Button
            label="+ 項目を追加"
            variant={ButtonVariantOption.Outlined}
            size={SizingWrapperStyle.SMALL}
            onClick={() => {
              setShowAddForm(true);
            }}
          />
        )}

        {showAddForm && (
          <AddFieldForm
            allFields={value?.fields}
            onAdd={handleAddField}
            onCancel={() => {
              setShowAddForm(false);
            }}
          />
        )}
      </Box>

      {/* 削除済み項目表示モーダル */}
      <DeletedFieldsModal
        open={showDeletedModal}
        deletedFields={deletedFields}
        onRestore={handleRestoreField}
        onClose={() => {
          setShowDeletedModal(false);
        }}
      />
    </Box>
  );
};

SavedMapPlotRequestDetailAdditionalFieldsConfig.displayName =
  "SavedMapPlotRequestDetailAdditionalFieldsConfig";
